import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaSave, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useHRAdminAuth } from '../../../hooks/useAdminAuth';
import { API_CONFIG } from '../../../utils/apiConfig';
import { COMPANIES } from '../../../utils/companyData';
import { db } from '../../../firebase';
import { collection, addDoc, updateDoc, doc, getDocs } from 'firebase/firestore';

import './AppointmentsPage.css';

const StaffAppointments = () => {
  const { currentUser } = useAuth();
  const { hasAccess, isLoading: authLoading, error: authError } = useHRAdminAuth();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    role: '',
    company: '',
    date: '',
    status: 'pending'
  });
  const [appointments, setAppointments] = useState([]);
  const [companies, setCompanies] = useState(COMPANIES);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const API_URL = `${API_CONFIG.BASE_URL}/api`;

  // Firebase fallback functions
  const updateAppointmentInFirebase = async (appointmentId, appointmentData) => {
    try {
      const appointmentDoc = doc(db, 'appointments', appointmentId);
      await updateDoc(appointmentDoc, {
        ...appointmentData,
        updatedAt: new Date().toISOString(),
        isFirebase: true
      });
      return { success: true, id: appointmentId };
    } catch (error) {
      console.error('Failed to update appointment in Firebase:', error);
      throw error;
    }
  };

  const createAppointmentInFirebase = async (appointmentData) => {
    try {
      const appointmentsCollection = collection(db, 'appointments');
      const docRef = await addDoc(appointmentsCollection, {
        ...appointmentData,
        createdAt: new Date().toISOString(),
        isFirebase: true
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Failed to create appointment in Firebase:', error);
      throw error;
    }
  };

  const loadFirebaseAppointments = async () => {
    try {
      const appointmentsCollection = collection(db, 'appointments');
      const snapshot = await getDocs(appointmentsCollection);
      const firebaseAppointments = [];
      
      snapshot.forEach((doc) => {
        const appointmentData = { _id: doc.id, ...doc.data() };
        firebaseAppointments.push(appointmentData);
      });
      
      return firebaseAppointments;
    } catch (error) {
      console.error('Failed to load Firebase appointments:', error);
      return [];
    }
  };

  useEffect(() => {
    // Wait for authentication to complete
    if (authLoading) {
      return;
    }

    // Check if user has access
    if (!hasAccess) {
      setError(authError || 'Access denied. HR Admin privileges required.');
      return;
    }

    // User has access, load data
    fetchCompanies();
    fetchData();
  }, [hasAccess, authLoading, authError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Add refresh functionality
  const handleRefresh = async () => {
    setError('');
    setSuccessMessage('');
    setIsLoading(true);
    try {
      await fetchCompanies();
      await fetchData();
      setSuccessMessage('Data refreshed successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Refresh failed:', err);
      setError('Failed to refresh data');
    }
  };

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const response = await axios.get(`${API_URL}/companies`, { headers });
      const apiCompanies = response.data.companies || response.data || [];
      
      if (apiCompanies.length > 0) {
        setCompanies(apiCompanies);
        setCompaniesLoading(false);
        return;
      }
    } catch (error) {
    }
    
    // Keep the hardcoded companies as fallback
    setCompaniesLoading(false);
  };

  const fetchData = async () => {
    // Security: Double-check access before API calls
    if (!hasAccess) {
      setError('Access denied. HR Admin privileges required.');
      return;
    }

    setIsLoading(true);
    setError(''); // Clear any previous errors
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };

      // Companies are now hardcoded, so we only need to fetch appointments
      try {
        // Security: Remove sensitive logging in production
        if (process.env.NODE_ENV === 'development') {
          console.log('Fetching appointments from API...');
        }
        const appointmentsRes = await axios.get(`${API_URL}/appointments`, { headers });
        const appointmentsData = Array.isArray(appointmentsRes.data) ? appointmentsRes.data : [];
        if (process.env.NODE_ENV === 'development') {
          console.log('Appointments fetched successfully:', appointmentsData.length, 'items');
        }
        
        // Process appointments to ensure company data is properly structured
        const processedAppointments = appointmentsData.map(appointment => {
          
          // If company is just an ID string, try to find the company object
          if (typeof appointment.company === 'string' && appointment.company.trim() !== '') {
            const company = companies.find(c => c._id === appointment.company || c.id === appointment.company);
            if (company) {
              return {
                ...appointment,
                company: {
                  _id: company._id || company.id,
                  name: company.name
                }
              };
            } else {
              // Company not found, set to null to avoid rendering issues
              return {
                ...appointment,
                company: null
              };
            }
          }
          
          // If company is an empty object or invalid, set to null
          if (typeof appointment.company === 'object' && (!appointment.company || Object.keys(appointment.company).length === 0)) {
            return {
              ...appointment,
              company: null
            };
          }
          
          // If company is an object but has no name, try to find it by ID
          if (typeof appointment.company === 'object' && appointment.company && (appointment.company._id || appointment.company.id)) {
            const companyId = appointment.company._id || appointment.company.id;
            const company = companies.find(c => c._id === companyId || c.id === companyId);
            if (company) {
              return {
                ...appointment,
                company: {
                  _id: company._id || company.id,
                  name: company.name
                }
              };
            } else {
              return {
                ...appointment,
                company: null
              };
            }
          }
          
          return appointment;
        });
        
        setAppointments(processedAppointments);
        if (process.env.NODE_ENV === 'development') {
          console.log('Appointments processed and set successfully');
        }
      } catch (appointmentsErr) {
        console.error('Failed to fetch appointments:', appointmentsErr);
        if (appointmentsErr.response?.status === 404 || appointmentsErr.message?.includes('404') || appointmentsErr.message?.includes('Not Found')) {
          console.warn('Appointments endpoint not available - loading from Firebase');
          // Load from Firebase as fallback
          const firebaseAppointments = await loadFirebaseAppointments();
          console.log('Firebase appointments loaded:', firebaseAppointments.length, 'items');
          const processedFirebaseAppointments = firebaseAppointments.map(appointment => {
            // Process company data for Firebase appointments
            if (typeof appointment.company === 'string' && appointment.company.trim() !== '') {
              const company = companies.find(c => c._id === appointment.company || c.id === appointment.company);
              if (company) {
                return {
                  ...appointment,
                  company: {
                    _id: company._id || company.id,
                    name: company.name
                  }
                };
              }
            }
            return appointment;
          });
          setAppointments(processedFirebaseAppointments);
          console.log('Firebase appointments processed and set successfully');
        } else {
          setError(`Failed to load appointments data: ${appointmentsErr.response?.data?.error || appointmentsErr.message}`);
        }
      }
    } catch (err) {
      console.error('Unexpected error in fetchData:', err);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Security: Input sanitization to prevent XSS
  const sanitizeInput = (input) => {
    if (typeof input !== 'string') return input;
    return input
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    setForm(prev => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Security: Check access before form submission
    if (!hasAccess) {
      setError('Access denied. HR Admin privileges required.');
      return;
    }
    
    if (companiesLoading) {
      setError('Please wait while companies are loading...');
      return;
    }
    
    // Security: Input validation and sanitization
    const sanitizedForm = {
      name: form.name?.trim() || '',
      phone: form.phone?.trim() || '',
      email: form.email?.trim() || '',
      role: form.role?.trim() || '',
      company: form.company?.trim() || '',
      date: form.date?.trim() || '',
      status: form.status?.trim() || 'pending'
    };
    
    if (!sanitizedForm.name || !sanitizedForm.role || !sanitizedForm.company || !sanitizedForm.date) {
      setError('Name, Role, Company, and Date are required');
      return;
    }
    if (sanitizedForm.company === '') {
      setError('Please select a valid company');
      return;
    }

    // Validate company ID exists in our companies list
    const selectedCompany = companies.find(c => c._id === sanitizedForm.company || c.id === sanitizedForm.company);
    
    if (!selectedCompany) {
      setError('Selected company is not valid. Please select a company from the list.');
      return;
    }

    // Security: Prepare sanitized form data
    const appointmentData = {
      ...sanitizedForm,
      company: {
        _id: selectedCompany._id || selectedCompany.id,
        name: selectedCompany.name
      }
    };

    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      
      const res = await axios.post(`${API_URL}/appointments`, appointmentData, { headers });
      
      setAppointments(prev => [...prev, res.data]);
      setForm({
        name: '',
        phone: '',
        email: '',
        role: '',
        company: '',
        date: '',
        status: 'pending'
      });
      setError('');
    } catch (err) {
      console.error('Failed to create appointment via API:', err);
      
      // If it's a 404 error, try Firebase fallback
      if (err.response?.status === 404 || err.message?.includes('404') || err.message?.includes('Not Found')) {
        console.warn('API endpoint not available - trying Firebase fallback');
        try {
          // Try to create in Firebase
          const result = await createAppointmentInFirebase(appointmentData);
          
          // Add to local state with proper company structure
          const newAppointment = {
            _id: result.id,
            ...appointmentData,
            isFirebase: true
          };
          
          setAppointments(prev => [...prev, newAppointment]);
          setForm({
            name: '',
            phone: '',
            email: '',
            role: '',
            company: '',
            date: '',
            status: 'pending'
          });
          setError('');
          
          // Show success message
          setError('Appointment created in Firebase (API endpoint unavailable). Data will sync when server is available.');
          setTimeout(() => setError(''), 3000);
        } catch (firebaseError) {
          console.error('Failed to create in Firebase:', firebaseError);
          setError('Failed to create appointment. Both API and Firebase creation failed.');
        }
      } else {
        const errorMessage = err.response?.data?.error || 'Failed to create appointment';
        setError(errorMessage);
        
        if (err.response?.status === 400) {
          // For 400 errors, try to provide more specific guidance
          if (errorMessage.includes('documentPath')) {
            setError('Server error: Invalid data format. Please check all fields and try again.');
          } else if (errorMessage.includes('company')) {
            setError('Company validation failed. Please select a different company and try again.');
          }
        }
      }
    }
  };

  const startEditing = (appointment) => {
    if (!appointment.name || !appointment.role || !appointment.date) {
      setError('Cannot edit: Appointment is missing required fields (Name, Role, or Date)');
      return;
    }
    
    const appointmentId = appointment._id || appointment.id;
    
    if (!appointmentId) {
      setError('Cannot edit: Appointment is missing ID');
      return;
    }
    
    // Check if this appointment was created locally but not saved to server
    if (appointment.isLocal || appointment.isFirebase) {
      setError('Cannot edit: This appointment was created locally and may not exist on the server. Please refresh the page and try again.');
      return;
    }
    
    setEditingId(appointmentId);
    const companyId = appointment.company?._id || appointment.company?.id || appointment.company || '';
    
    setEditForm({
      name: appointment.name || '',
      phone: appointment.phone || '',
      email: appointment.email || '',
      role: appointment.role || '',
      company: companyId,
      status: appointment.status || 'pending',
      date: appointment.date ? new Date(appointment.date).toISOString().split('T')[0] : ''
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleUpdate = async () => {
    // Security: Check access before update
    if (!hasAccess) {
      setError('Access denied. HR Admin privileges required.');
      return;
    }
    
    if (!editingId) {
      setError('Cannot update: No appointment ID found');
      return;
    }
    
    const errors = [];
    if (!editForm.name) errors.push('Name');
    if (!editForm.role) errors.push('Role');
    if (!editForm.company) errors.push('Company');
    if (!editForm.date) errors.push('Date');

    if (errors.length > 0) {
      setError(`${errors.join(', ')} ${errors.length > 1 ? 'are' : 'is'} required`);
      return;
    }
    
    // Check if this is a Firebase appointment - if so, skip server verification
    const currentAppointment = appointments.find(app => (app._id || app.id) === editingId);
    const isFirebaseAppointment = currentAppointment?.isFirebase || currentAppointment?.isLocal;
    
    if (!isFirebaseAppointment) {
      // Only verify server existence for non-Firebase appointments
      try {
        const idToken = await currentUser.getIdToken(true);
        const headers = { Authorization: `Bearer ${idToken}` };
        
        // Try to fetch the appointment to verify it exists
        await axios.get(`${API_URL}/appointments/${editingId}`, { headers });
      } catch (verifyError) {
        console.error('Appointment verification failed:', verifyError);
        if (verifyError.response?.status === 404) {
          setError('Cannot update: Appointment not found on server. It may have been deleted or never saved. Refreshing the list...');
          // Cancel editing mode since the appointment doesn't exist
          setEditingId(null);
          setEditForm({});
          // Automatically refresh the appointments list to remove the phantom appointment
          setTimeout(() => {
            fetchData();
            setError('');
          }, 2000);
          return;
        }
        // If it's not a 404, continue with the update attempt
      }
    }

    // Validate company ID exists in our companies list
    const selectedCompany = companies.find(c => c._id === editForm.company || c.id === editForm.company);
    if (!selectedCompany) {
      setError('Selected company is not valid. Please select a company from the list.');
      return;
    }

    // Prepare update data - include both company ID and name for proper display
    const updateData = {
      ...editForm,
      company: {
        _id: selectedCompany._id || selectedCompany.id,
        name: selectedCompany.name
      }
    };

    // If this is a Firebase appointment, update directly in Firebase
    if (isFirebaseAppointment) {
      try {
        // Try to update in Firebase
        await updateAppointmentInFirebase(editingId, updateData);
        
        // Update local state
        setAppointments(prev => 
          prev.map(app => (app._id || app.id) === editingId ? { ...app, ...updateData, isFirebase: true } : app)
        );
        setEditingId(null);
        setEditForm({});
        setError('');
        
        // Show success message
        setError('Appointment updated in Firebase successfully.');
        setTimeout(() => setError(''), 3000);
      } catch (firebaseError) {
        console.error('Failed to update in Firebase:', firebaseError);
        setError('Failed to update appointment in Firebase. Please try again.');
      }
      return;
    }

    // For server appointments, try API first
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      const res = await axios.put(`${API_URL}/appointments/${editingId}`, updateData, { headers });
      
      setAppointments(prev => 
        prev.map(app => (app._id || app.id) === editingId ? res.data : app)
      );
      setEditingId(null);
      setEditForm({});
      setError('');
    } catch (err) {
      console.error('Failed to update appointment via API:', err);
      
      // If it's a 404 error, try Firebase fallback
      if (err.response?.status === 404 || err.message?.includes('404') || err.message?.includes('Not Found')) {
        console.warn('API endpoint not available - trying Firebase fallback');
        try {
          // Try to update in Firebase
          await updateAppointmentInFirebase(editingId, updateData);
          
          // Update local state
          setAppointments(prev => 
            prev.map(app => (app._id || app.id) === editingId ? { ...app, ...updateData, isFirebase: true } : app)
          );
          setEditingId(null);
          setEditForm({});
          setError('');
          
          // Show success message
          setError('Appointment updated in Firebase (API endpoint unavailable). Data will sync when server is available.');
          setTimeout(() => setError(''), 3000);
        } catch (firebaseError) {
          console.error('Failed to update in Firebase:', firebaseError);
          setError('Failed to update appointment. Both API and Firebase updates failed.');
        }
      } else {
        setError(err.response?.data?.error || 'Failed to update appointment. Please check all fields.');
      }
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = sanitizeInput(value);
    setEditForm(prev => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleDelete = async (id) => {
    // Security: Check access before delete
    if (!hasAccess) {
      setError('Access denied. HR Admin privileges required.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      const idToken = await currentUser.getIdToken(true);
      const headers = { Authorization: `Bearer ${idToken}` };
      
      await axios.delete(`${API_URL}/appointments/${id}`, { headers });
      
      setAppointments(prev => prev.filter(app => (app._id || app.id) !== id));
    } catch (err) {
      setError('Failed to delete appointment');
      console.error(err);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Show loading while authentication is being checked
  if (authLoading) {
    return (
      <div className="appointments-page">
        <div className="page-header">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <h2>Staff Appointments</h2>
        </div>
        <div className="loading-indicator">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Verifying access...</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Please wait while we check your permissions</div>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied if no proper permissions
  if (!hasAccess) {
    return (
      <div className="appointments-page">
        <div className="page-header">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <h2>Staff Appointments</h2>
        </div>
        <div className="error-message">
          <h3>Access Denied</h3>
          <p>You don't have permission to access HR Admin features.</p>
          <p>Required: HR Admin, Super Admin, or Admin with HR department access.</p>
        </div>
      </div>
    );
  }

  if (isLoading && appointments.length === 0) {
    return (
      <div className="appointments-page">
        <div className="page-header">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <h2>Staff Appointments</h2>
        </div>
        <div className="loading-indicator">
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>Loading appointments...</div>
            <div style={{ fontSize: '0.9rem', color: '#666' }}>Please wait while we fetch your data</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="appointments-page">
      <div className="page-header">
        <button onClick={handleBack} className="appoimants-back-button">
          <FaArrowLeft /> Back
        </button>
        <h2>Staff Appointments</h2>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message" style={{ 
        backgroundColor: '#d4edda', 
        color: '#155724', 
        padding: '0.75rem 1rem', 
        borderRadius: '4px', 
        marginBottom: '1rem',
        border: '1px solid #c3e6cb'
      }}>{successMessage}</div>}

      <form className="appointment-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <input
            type="text"
            name="name"
            placeholder="Full Name*"
            value={form.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            value={form.phone}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <input
            type="text"
            name="role"
            placeholder="Role/Position*"
            value={form.role}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <input
            type="date"
            name="date"
            placeholder="Appointment Date*"
            value={form.date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <select
            name="company"
            value={form.company}
            onChange={handleChange}
            required
            disabled={companiesLoading}
          >
            <option value="">
              {companiesLoading ? 'Loading companies...' : 'Select Company*'}
            </option>
            {Array.isArray(companies) && companies.map(company => (
              <option key={company._id || company.id || `company-${company.name}`} value={company._id || company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
          >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <button type="submit" className="submit-button">
          Add Staff Appointment
        </button>
      </form>

      <div className="appoiment-list">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2>Saved Appointments</h2>
          <button 
            onClick={handleRefresh} 
            disabled={isLoading}
            style={{ 
              padding: '0.5rem 1rem', 
              backgroundColor: isLoading ? '#6c757d' : '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
        <div className="table-container">
          <table className="appoiment-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Role</th>
              <th>Date</th>
              <th>Company</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {appointments.length === 0 ? (
              <tr key="no-appointments">
                <td colSpan="8" className="no-data">No appointments found</td>
              </tr>
            ) : (
              Array.isArray(appointments) && appointments.map((app, index) => (
                <tr key={app._id || app.id || `appointment-${index}`}>
                  {editingId === (app._id || app.id) ? (
                    <>
                      <td>
                        <input
                          type="text"
                          name="name"
                          value={editForm.name}
                          onChange={handleEditChange}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="tel"
                          name="phone"
                          value={editForm.phone}
                          onChange={handleEditChange}
                        />
                      </td>
                      <td>
                        <input
                          type="email"
                          name="email"
                          value={editForm.email}
                          onChange={handleEditChange}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          name="role"
                          value={editForm.role}
                          onChange={handleEditChange}
                          required
                        />
                      </td>
                      <td>
                        <input
                          type="date"
                          name="date"
                          value={editForm.date}
                          onChange={handleEditChange}
                          required
                        />
                      </td>
                      <td>
                        <select
                          name="company"
                          value={editForm.company}
                          onChange={handleEditChange}
                          required
                        >
                          <option value="">Select Company*</option>
                          {Array.isArray(companies) && companies.map(company => (
                            <option key={company._id || company.id || `company-${company.name}`} value={company._id || company.id}>
                              {company.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          name="status"
                          value={editForm.status}
                          onChange={handleEditChange}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="actions">
                        <button onClick={handleUpdate} className="save">
                          <FaSave /> Save
                        </button>
                        <button onClick={cancelEditing} className="cancel">
                          <FaTimes /> Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>{app.name}</td>
                      <td>{app.phone || '-'}</td>
                      <td>{app.email || '-'}</td>
                      <td>{app.role}</td>
                      <td>{app.date ? new Date(app.date).toLocaleDateString() : '-'}</td>
                      <td>
                        {(() => {
                          
                          // Check if company is null, undefined, or empty object
                          if (!app.company || (typeof app.company === 'object' && Object.keys(app.company).length === 0)) {
                            return '-';
                          }
                          
                          // If company is an object with name property
                          if (app.company?.name && typeof app.company.name === 'string') {
                            return app.company.name;
                          }
                          
                          // If company is just an ID (string), look it up in companies array
                          if (typeof app.company === 'string' && app.company.trim() !== '') {
                            const company = companies.find(c => c._id === app.company || c.id === app.company);
                            return company?.name || app.company;
                          }
                          
                          // If company is an object but no name, try to extract ID
                          if (typeof app.company === 'object' && (app.company._id || app.company.id)) {
                            const companyId = app.company._id || app.company.id;
                            const company = companies.find(c => c._id === companyId || c.id === companyId);
                            return company?.name || companyId;
                          }
                          
                          return '-';
                        })()}
                      </td>
                      <td className={`status-${app.status}`}>
                        {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                      </td>
                      <td className="actions">
                        <button onClick={() => startEditing(app)} className="edit">
                          <FaEdit />
                        </button>
                        <button onClick={() => handleDelete(app._id || app.id)} className="delete">
                          <FaTrash />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </div>
      
      {/* Mobile Card Layout */}
      <div className="mobile-cards-container">
        {isLoading ? (
          <div className="mobile-loading">
            <div className="mobile-loading-spinner"></div>
          </div>
        ) : appointments.length === 0 ? (
          <div className="mobile-empty-state">
            <div className="mobile-empty-state-icon">📅</div>
            <div className="mobile-empty-state-text">No appointments found</div>
            <div className="mobile-empty-state-subtext">Create your first appointment using the form above</div>
          </div>
        ) : (
          appointments.map((app, index) => (
            <div key={app._id || app.id || `appointment-${index}`} className="mobile-card">
              <div className="card-header">
                <h3 className="card-title">{app.name}</h3>
                <span className={`card-status ${app.status}`}>
                  {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                </span>
              </div>
              
              <div className="card-content">
                <div className="card-row">
                  <span className="card-label">Phone</span>
                  <span className="card-value phone">{app.phone || '-'}</span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Email</span>
                  <span className="card-value email">{app.email || '-'}</span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Role</span>
                  <span className="card-value">
                    <span className={`card-role ${app.role?.toLowerCase().replace(' ', '-')}`}>
                      {app.role}
                    </span>
                  </span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Date</span>
                  <span className="card-value">
                    <span className="card-date">
                      {app.date ? new Date(app.date).toLocaleDateString() : '-'}
                    </span>
                  </span>
                </div>
                
                <div className="card-row">
                  <span className="card-label">Company</span>
                  <span className="card-value">
                    {(() => {
                      if (!app.company || (typeof app.company === 'object' && Object.keys(app.company).length === 0)) {
                        return '-';
                      }
                      if (app.company?.name && typeof app.company.name === 'string') {
                        return app.company.name;
                      }
                      if (typeof app.company === 'string' && app.company.trim() !== '') {
                        const company = companies.find(c => c._id === app.company || c.id === app.company);
                        return company?.name || app.company;
                      }
                      if (typeof app.company === 'object' && (app.company._id || app.company.id)) {
                        const companyId = app.company._id || app.company.id;
                        const company = companies.find(c => c._id === companyId || c.id === companyId);
                        return company?.name || companyId;
                      }
                      return '-';
                    })()}
                  </span>
                </div>
              </div>
              
              <div className="card-actions">
                <button 
                  onClick={() => startEditing(app)} 
                  className="card-action-btn edit"
                >
                  <FaEdit /> Edit
                </button>
                <button 
                  onClick={() => handleDelete(app._id || app.id)} 
                  className="card-action-btn delete"
                >
                  <FaTrash /> Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default StaffAppointments;